import { Observable, Subject } from 'rxjs';

import FormStatus from './FormStatus';
import FormValidity from './FormValidity';
import ControlValidator from './validator/CustomValidator';

abstract class AbstractFormControl<T = unknown> {
  private currentValue: T;

  private readonly valueChangesInternal: Subject<T>;

  protected state: FormStatus;

  protected validity: FormValidity;

  public errors: Record<string, string[]> | null;

  constructor(
    protected readonly initialValue: T | null,
    protected readonly validators: ControlValidator<T>[] = [],
  ) {
    this.valueChangesInternal = new Subject<T>();
    this.currentValue = initialValue;
    this.state = FormStatus.Pristine;
    this.validity = FormValidity.Valid;
  }

  public get value(): T {
    return this.currentValue;
  }

  public set value(newValue: T) {
    this.update(newValue);
  }

  public get valueChanges(): Observable<T> {
    return this.valueChangesInternal;
  }

  public update(v: T, options = { emitEvent: true }) {
    this.currentValue = v;
    this.state = FormStatus.Dirty;
    this.runValidators();
    if (options?.emitEvent) {
      this.valueChangesInternal.next(this.currentValue);
    }
  }

  public reset(withInitialValue = false, options = { emitEvent: true }) {
    this.currentValue = withInitialValue ? this.initialValue : null;
    this.state = FormStatus.Pristine;
    if (options?.emitEvent) {
      this.valueChangesInternal.next(this.currentValue);
    }
  }

  /**
   * @returns `true` is the value has not been modified and `false` otherwise
   */
  public get pristine() {
    return this.state === FormStatus.Pristine;
  }

  /**
   * @returns `true` is the value has been modified and `false` otherwise
   */
  public get dirty() {
    return this.state === FormStatus.Dirty;
  }

  public get valid() {
    return this.validity === FormValidity.Valid;
  }

  public get invalid() {
    return this.validity === FormValidity.Invalid;
  }

  /**
   * @returns `true` is the validation process is still running `false` otherwise
   */
  public get pending() {
    return this.validity === FormValidity.Pending;
  }

  public get firstError(): string | null {
    if (this.errors == null) {
      return null;
    }
    return Object.values(this.errors)[0][0];
  }

  public get errorMessages(): string[] {
    if (this.errors == null) {
      return null;
    }
    return Object.values(this.errors).flat();
  }

  public async runValidators(): Promise<FormValidity> {
    this.validity = FormValidity.Pending;
    const errors = {};
    for (const validator of this.validators) {
      const error = await validator(this.value);
      if (error != null) {
        for (const [key, value] of Object.entries(error)) {
          if (errors[key] == null) {
            errors[key] = [value];
          } else {
            errors[key].push(value);
          }
        }
      }
    }
    this.errors = errors;
    this.validity = Object.keys(this.errors).length === 0 ? FormValidity.Valid : FormValidity.Invalid;
    return this.validity;
  }

  public markAsPristine() {
    this.state = FormStatus.Pristine;
  }

  public markAsDirty() {
    this.state = FormStatus.Dirty;
  }
}

export default AbstractFormControl;
